// =============================================================================
// features/sales/campaigns/detail/useCampaignSettingsForm.ts — Estado do
// formulário da aba Configuração + diff contra o registro atual.
//
// `allowedFields(status)` espelha EXATAMENTE `allowedPatchFields` do backend
// (apps/api/src/modules/sales/campaigns/service.ts, fora de `files_allowed`
// — lido, não editado): draft/ready editam tudo; active/paused só os campos
// operacionais; completed/cancelled não editam nada. Mandar um campo fora da
// lista permitida daria 409 no servidor — este hook evita a viagem
// desnecessária desabilitando o campo na UI, mas o servidor continua sendo
// a fonte de verdade (nunca confiar só na checagem do cliente).
// =============================================================================

import type {
  AssignmentStrategy,
  Campaign,
  CampaignPatchBody,
  CampaignStatus,
  CampaignWeeklyWindow,
} from '@elemento/shared-schemas';
import * as React from 'react';

const CORE_FIELDS = [
  'name',
  'description',
  'productId',
  'ownerUserId',
  'assignmentStrategy',
] as const;
const OPERATIONAL_FIELDS = [
  'priority',
  'weeklyWindow',
  'timezone',
  'maxAttempts',
  'minIntervalMinutes',
] as const;

type FieldKey = (typeof CORE_FIELDS)[number] | (typeof OPERATIONAL_FIELDS)[number];

export function allowedSettingsFields(status: CampaignStatus): readonly FieldKey[] {
  if (status === 'draft' || status === 'ready') return [...CORE_FIELDS, ...OPERATIONAL_FIELDS];
  if (status === 'active' || status === 'paused') return OPERATIONAL_FIELDS;
  return [];
}

export interface SettingsFormValues {
  name: string;
  description: string;
  productId: string;
  ownerUserId: string;
  assignmentStrategy: AssignmentStrategy;
  priority: number;
  timezone: string;
  maxAttempts: number;
  minIntervalMinutes: number;
  weeklyWindow: CampaignWeeklyWindow;
}

function toFormValues(campaign: Campaign): SettingsFormValues {
  return {
    name: campaign.name,
    description: campaign.description ?? '',
    productId: campaign.productId ?? '',
    ownerUserId: campaign.ownerUserId,
    assignmentStrategy: campaign.assignmentStrategy,
    priority: campaign.priority,
    timezone: campaign.timezone,
    maxAttempts: campaign.maxAttempts,
    minIntervalMinutes: campaign.minIntervalMinutes,
    weeklyWindow: campaign.weeklyWindow,
  };
}

const FIELD_LABEL: Record<FieldKey, string> = {
  name: 'Nome',
  description: 'Descrição',
  productId: 'Produto',
  ownerUserId: 'Responsável',
  assignmentStrategy: 'Estratégia de distribuição',
  priority: 'Prioridade',
  timezone: 'Fuso horário',
  maxAttempts: 'Tentativas máximas',
  minIntervalMinutes: 'Intervalo mínimo (min)',
  weeklyWindow: 'Janela semanal',
};

function summarizeWeeklyWindow(w: CampaignWeeklyWindow): string {
  const days = Object.keys(w);
  return days.length === 0 ? 'nenhum dia' : `${days.length} dia(s) configurado(s)`;
}

function fieldDisplay(key: FieldKey, values: SettingsFormValues): string {
  if (key === 'weeklyWindow') return summarizeWeeklyWindow(values.weeklyWindow);
  if (key === 'productId') return values.productId || '—';
  return String(values[key]);
}

export interface UseCampaignSettingsFormResult {
  values: SettingsFormValues;
  setField: <K extends keyof SettingsFormValues>(key: K, value: SettingsFormValues[K]) => void;
  allowed: readonly FieldKey[];
  isFieldAllowed: (key: FieldKey) => boolean;
  buildPatch: () => CampaignPatchBody | null;
  diffForConfirm: () => { label: string; before: string; after: string }[];
  resetToServer: () => void;
}

export function useCampaignSettingsForm(campaign: Campaign): UseCampaignSettingsFormResult {
  const serverValues = React.useMemo(() => toFormValues(campaign), [campaign]);
  const [values, setValues] = React.useState<SettingsFormValues>(serverValues);

  // Dependência deliberadamente restrita a `campaign.updatedAt` (não
  // `serverValues`, que muda de identidade a cada render por vir de
  // `useMemo` com objeto novo): só ressincroniza quando o registro do
  // servidor muda de fato (após um PATCH bem-sucedido ou refetch) — nunca
  // sobrescreve digitação em curso.
  React.useEffect(() => {
    setValues(serverValues);
  }, [campaign.updatedAt]);

  const allowed = allowedSettingsFields(campaign.status);

  function setField<K extends keyof SettingsFormValues>(
    key: K,
    value: SettingsFormValues[K],
  ): void {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  function isFieldAllowed(key: FieldKey): boolean {
    return allowed.includes(key);
  }

  function changedAllowedFields(): FieldKey[] {
    return allowed.filter((key) => {
      if (key === 'weeklyWindow') {
        return JSON.stringify(values.weeklyWindow) !== JSON.stringify(serverValues.weeklyWindow);
      }
      return values[key] !== serverValues[key];
    });
  }

  function buildPatch(): CampaignPatchBody | null {
    const changed = changedAllowedFields();
    if (changed.length === 0) return null;

    const patch: CampaignPatchBody = {};
    for (const key of changed) {
      switch (key) {
        case 'name':
          patch.name = values.name;
          break;
        case 'description':
          patch.description = values.description.trim().length > 0 ? values.description : null;
          break;
        case 'productId':
          patch.productId = values.productId.trim().length > 0 ? values.productId : null;
          break;
        case 'ownerUserId':
          patch.ownerUserId = values.ownerUserId;
          break;
        case 'assignmentStrategy':
          patch.assignmentStrategy = values.assignmentStrategy;
          break;
        case 'priority':
          patch.priority = values.priority;
          break;
        case 'timezone':
          patch.timezone = values.timezone;
          break;
        case 'maxAttempts':
          patch.maxAttempts = values.maxAttempts;
          break;
        case 'minIntervalMinutes':
          patch.minIntervalMinutes = values.minIntervalMinutes;
          break;
        case 'weeklyWindow':
          patch.weeklyWindow = values.weeklyWindow;
          break;
      }
    }
    return patch;
  }

  function diffForConfirm(): { label: string; before: string; after: string }[] {
    return changedAllowedFields().map((key) => ({
      label: FIELD_LABEL[key],
      before: fieldDisplay(key, serverValues),
      after: fieldDisplay(key, values),
    }));
  }

  function resetToServer(): void {
    setValues(serverValues);
  }

  return { values, setField, allowed, isFieldAllowed, buildPatch, diffForConfirm, resetToServer };
}
